import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { LocalAgentCertificate } from '../../core/services/local-agent.service';

export interface TokenFisicoCertificateModalData {
  certificates: LocalAgentCertificate[];
}

@Component({
  selector: 'app-token-fisico-certificate-modal',
  templateUrl: './token-fisico-certificate-modal.component.html',
  styleUrls: ['./token-fisico-certificate-modal.component.css'],
  standalone: false
})
export class TokenFisicoCertificateModalComponent {
  selectedCertificate: LocalAgentCertificate | null = null;

  constructor(
    public dialogRef: MatDialogRef<TokenFisicoCertificateModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TokenFisicoCertificateModalData
  ) {}

  closeDialog(): void {
    this.dialogRef.close();
  }

  selectCertificate(cert: LocalAgentCertificate): void {
    this.selectedCertificate = cert;
  }

  confirmSelection(): void {
    if (this.selectedCertificate) {
      this.dialogRef.close({ certificate: this.selectedCertificate });
    }
  }

  displayName(cert: LocalAgentCertificate): string {
    return cert.friendlyName || this.extractCN(cert.subjectDN) || cert.thumbprint || 'Certificado';
  }

  /**
   * Extrae el CN (Common Name) del subjectDN.
   */
  private extractCN(subjectDN?: string): string | null {
    if (!subjectDN) return null;
    const match = subjectDN.match(/CN=([^,]+)/i);
    return match ? match[1].trim() : null;
  }

  /**
   * Detecta el tipo/uso del certificado basándose en el OU del subjectDN.
   * - TOKEN FISICO → Verde
   * - TOKEN VIRTUAL → Azul
   * - Otros → Índigo (muestra el valor del OU)
   */
  getCertificateType(cert: LocalAgentCertificate): { label: string; cssClass: string } | null {
    const ou = this.extractOU(cert.subjectDN);
    if (!ou) return null;

    const ouLower = ou.toLowerCase();

    if (ouLower.includes('token fisico') || ouLower.includes('Certifirma Digital Físico')) {
      return { label: 'Certifirma Digital Físico', cssClass: 'badge-fisico' };
    }
    if (ouLower.includes('Certifirma Digital Virtual')) {
      return { label: 'Certifirma Digital Virtual', cssClass: 'badge-virtual' };
    }
    return { label: ou, cssClass: 'badge-otro' };
  }

  /**
   * Extrae el valor del campo OU del subjectDN.
   */
  private extractOU(subjectDN?: string): string | null {
    if (!subjectDN) return null;
    const match = subjectDN.match(/OU=([^,]+)/i);
    return match ? match[1].trim() : null;
  }

  /**
   * Formatea el número de serie para mostrar solo los últimos 8 caracteres.
   */
  formatSerial(serial?: string): string {
    if (!serial) return '';
    if (serial.length <= 8) return serial;
    return '...' + serial.slice(-8).toUpperCase();
  }

  /**
   * Extrae el nombre del emisor (CN y O) del issuerDN.
   */
  extractIssuerName(issuerDN?: string): string {
    if (!issuerDN) return '';
    const cnMatch = issuerDN.match(/CN=([^,]+)/i);
    const oMatch = issuerDN.match(/O=([^,]+)/i);
    const cn = cnMatch ? cnMatch[1].trim() : '';
    const o = oMatch ? oMatch[1].trim() : '';
    if (cn && o) return `${cn} - ${o}`;
    return cn || o || issuerDN;
  }

  /**
   * Formatea la fecha de vencimiento a un formato legible.
   */
  formatDate(dateStr?: string): string {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-CO', { 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  }
}
